import { useState } from 'react';
import { MousePointerClick, Palette, PaintBucket, Sparkles } from 'lucide-react';
import type { KreatorSettingsData } from '../KreatorPage';
import { SettingsSaveBar } from '../../components/SettingsSaveBar';
import { SectionHeader } from '../../components/SectionHeader';
import { createTenantKreatorAdapter, type KreatorApiAdapter } from '../../lib/kreator-api-adapter';
import { normalizeHexColor, prefixHexColor, safeColorForPicker } from './builder-v2/utils/color-utils';

interface Props {
  integrationId: string;
  settings: KreatorSettingsData | null;
  onSaved: () => void;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
  apiAdapter?: KreatorApiAdapter;
  showButtonColors?: boolean;
}

function ColorInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <input
        type="color"
        value={safeColorForPicker(value, '#ffffff')}
        onChange={(e) => onChange(e.target.value)}
        className="w-12 h-12 rounded-lg border border-border bg-transparent cursor-pointer"
      />
      <span className="text-xs text-text-secondary text-center leading-tight">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(prefixHexColor(e.target.value))}
        onBlur={(e) => onChange(normalizeHexColor(e.target.value))}
        className="input w-24 text-xs font-mono text-center"
      />
    </div>
  );
}

function GradientInput({
  label,
  gradient,
  onChange,
}: {
  label: string;
  gradient: { hex1: string; hex2: string };
  onChange: (g: { hex1: string; hex2: string }) => void;
}) {
  const hex1 = gradient.hex1;
  const hex2 = gradient.hex2;

  return (
    <div className="p-4 rounded-lg border border-border-subtle bg-surface-raised">
      <span className="text-sm font-medium text-text-secondary block mb-3">{label}</span>
      <div className="space-y-3">
        <div
          className="h-10 rounded-lg"
          style={{ background: `linear-gradient(90deg, ${hex1}, ${hex2})` }}
        />
        <div className="flex gap-3">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={safeColorForPicker(hex1, '#ffffff')}
              onChange={(e) => onChange({ hex1: e.target.value, hex2 })}
              className="w-8 h-8 rounded border border-border bg-transparent cursor-pointer"
            />
            <input
              type="text"
              value={hex1}
              onChange={(e) => onChange({ hex1: prefixHexColor(e.target.value), hex2 })}
              onBlur={(e) => onChange({ hex1: normalizeHexColor(e.target.value), hex2 })}
              className="input w-24 text-xs font-mono"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={safeColorForPicker(hex2, '#ffffff')}
              onChange={(e) => onChange({ hex1, hex2: e.target.value })}
              className="w-8 h-8 rounded border border-border bg-transparent cursor-pointer"
            />
            <input
              type="text"
              value={hex2}
              onChange={(e) => onChange({ hex1, hex2: prefixHexColor(e.target.value) })}
              onBlur={(e) => onChange({ hex1, hex2: normalizeHexColor(e.target.value) })}
              className="input w-24 text-xs font-mono"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function KreatorColorsSection({ integrationId, settings, onSaved, showError, apiAdapter, showButtonColors = true }: Props) {
  const adapter = apiAdapter ?? createTenantKreatorAdapter(integrationId);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const settingsRecord = (settings?.settings ?? {}) as Record<string, unknown>;
  const [form, setForm] = useState({
    backgroundColor: settings?.settings.backgroundColor || '#ffffff',
    fontColor: settings?.settings.fontColor || '#1a1a1a',
    hoverTextColor: settingsRecord.hoverTextColor as string || '#ffffff',
    buttonBackgroundColor: (settingsRecord.buttonBackgroundColor as string) || '#3b82f6',
    buttonBackgroundHoverColor: (settingsRecord.buttonBackgroundHoverColor as string) || '#3b82f6',
    buttonTextColor: (settingsRecord.buttonTextColor as string) || '#ffffff',
    buttonTextHoverColor: (settingsRecord.buttonTextHoverColor as string) || '#ffffff',
    primary: settings?.colors.primary || '#3b82f6',
    additional1: settings?.colors.additional1 || '#10b981',
    additional2: settings?.colors.additional2 || '#f59e0b',
    additional3: settings?.colors.additional3 || '#ef4444',
    primaryGradient: settings?.colors.primaryGradient || { hex1: '#3b82f6', hex2: '#8b5cf6' },
    additionalGradient: settings?.colors.additionalGradient || { hex1: '#10b981', hex2: '#06b6d4' },
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await adapter.updateColors({
        primary: normalizeHexColor(form.primary),
        additional1: normalizeHexColor(form.additional1),
        additional2: normalizeHexColor(form.additional2),
        additional3: normalizeHexColor(form.additional3),
        primaryGradient: {
          hex1: normalizeHexColor(form.primaryGradient.hex1),
          hex2: normalizeHexColor(form.primaryGradient.hex2),
        },
        additionalGradient: {
          hex1: normalizeHexColor(form.additionalGradient.hex1),
          hex2: normalizeHexColor(form.additionalGradient.hex2),
        },
      });
      const settingsPayload: Record<string, unknown> = {
        backgroundColor: normalizeHexColor(form.backgroundColor),
        fontColor: normalizeHexColor(form.fontColor),
        hoverTextColor: form.hoverTextColor ? normalizeHexColor(form.hoverTextColor) : undefined,
      };
      if (showButtonColors) {
        settingsPayload.buttonBackgroundColor = form.buttonBackgroundColor ? normalizeHexColor(form.buttonBackgroundColor) : undefined;
        settingsPayload.buttonBackgroundHoverColor = form.buttonBackgroundHoverColor ? normalizeHexColor(form.buttonBackgroundHoverColor) : undefined;
        settingsPayload.buttonTextColor = form.buttonTextColor ? normalizeHexColor(form.buttonTextColor) : undefined;
        settingsPayload.buttonTextHoverColor = form.buttonTextHoverColor ? normalizeHexColor(form.buttonTextHoverColor) : undefined;
      }
      await adapter.updateSettings(settingsPayload);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
      onSaved();
      if (adapter.isTenantScoped) {
        try {
          await adapter.purgeWpCache();
        } catch (purgeErr: unknown) {
          const msg = (purgeErr as { response?: { data?: { error?: string } } })?.response?.data?.error;
          try {
            const diag = await adapter.diagnoseWp();
            showError(diag.data.ok ? 'Zapisano, ale cache nie został wyczyszczony. Wyczyść cache ręcznie w WordPress.' : `Zapisano, ale cache WP nie działa: ${diag.data.error}`);
          } catch {
            showError(msg ? `Zapisano, ale czyszczenie cache nie powiodło się: ${msg}` : 'Zapisano ustawienia, ale nie udało się wyczyścić cache WordPress.');
          }
        }
      }
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
          <Palette className="w-6 h-6 text-accent-primary" />
          Kolory
        </h1>
        <p className="text-text-secondary mt-1">Paleta kolorów sklepu</p>
      </div>

      {/* Main Colors */}
      <div className="card">
        <SectionHeader
          icon={Palette}
          title="Kolory podstawowe"
          description="Akcent główny i trzy kolory pomocnicze"
          accent="secondary"
        />
        <div className="flex flex-wrap gap-6">
          <ColorInput
            label="Kolor główny"
            value={form.primary}
            onChange={(v) => setForm({ ...form, primary: v })}
          />
          <ColorInput
            label="Dodatkowy #1"
            value={form.additional1}
            onChange={(v) => setForm({ ...form, additional1: v })}
          />
          <ColorInput
            label="Dodatkowy #2"
            value={form.additional2}
            onChange={(v) => setForm({ ...form, additional2: v })}
          />
          <ColorInput
            label="Dodatkowy #3"
            value={form.additional3}
            onChange={(v) => setForm({ ...form, additional3: v })}
          />
        </div>
      </div>

      {/* Background & Font Color */}
      <div className="card">
        <SectionHeader
          icon={PaintBucket}
          title="Kolory tła i tekstu"
          description="Główne kolory tła strony i domyślny kolor tekstu"
        />
        <div className="flex flex-wrap gap-6">
          <ColorInput
            label="Kolor tła"
            value={form.backgroundColor}
            onChange={(v) => setForm({ ...form, backgroundColor: v })}
          />
          <ColorInput
            label="Kolor tekstu"
            value={form.fontColor}
            onChange={(v) => setForm({ ...form, fontColor: v })}
          />
          <ColorInput
            label="Kolor linków hover"
            value={form.hoverTextColor}
            onChange={(v) => setForm({ ...form, hoverTextColor: v })}
          />
        </div>
      </div>

      {/* Gradients */}
      <div className="card">
        <SectionHeader
          icon={Sparkles}
          title="Gradienty"
          description="Liniowe przejścia kolorów do przycisków, badge'ów i sekcji"
          accent="tertiary"
        />
        <div className="space-y-3">
          <GradientInput
            label="Gradient podstawowy"
            gradient={form.primaryGradient}
            onChange={(g) => setForm({ ...form, primaryGradient: g })}
          />
          <GradientInput
            label="Gradient dodatkowy"
            gradient={form.additionalGradient}
            onChange={(g) => setForm({ ...form, additionalGradient: g })}
          />
        </div>
      </div>

      {showButtonColors && (
        <div className="card">
          <SectionHeader
            icon={MousePointerClick}
            title="Kolory buttonów"
            description="Dedykowane kolory tła i tekstu dla przycisków w sklepie"
            accent="secondary"
          />
          <div className="flex flex-wrap gap-6">
            <ColorInput
              label="Tło"
              value={form.buttonBackgroundColor}
              onChange={(v) => setForm({ ...form, buttonBackgroundColor: v })}
            />
            <ColorInput
              label="Tło hover"
              value={form.buttonBackgroundHoverColor}
              onChange={(v) => setForm({ ...form, buttonBackgroundHoverColor: v })}
            />
            <ColorInput
              label="Tekst"
              value={form.buttonTextColor}
              onChange={(v) => setForm({ ...form, buttonTextColor: v })}
            />
            <ColorInput
              label="Tekst hover"
              value={form.buttonTextHoverColor}
              onChange={(v) => setForm({ ...form, buttonTextHoverColor: v })}
            />
          </div>
        </div>
      )}

      <SettingsSaveBar saving={saving} status={saveStatus} onSave={handleSave} label="Zapisz kolory" />
    </div>
  );
}
